// Dependencies
import React, { useState } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';

// Utils
import analytics from '@nerdwallet/analytics';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Tooltip, Stack, Type, Box, Text } from '@nerdwallet/currency';
import { LOAN_TYPE_VA, LOAN_TYPE_FHA } from '@nerdwallet/home-constants';
import { getAnnualInterestRateForCreditScore } from '@nerdwallet/mortgage-utils/rate-adjustment';
import { Tracking, Num } from '@nerdwallet/nw-client-lib';
import { formatCurrency, formatPercent } from '@nerdwallet/nw-client-lib/num';
import { ControlledAccordion } from '@nerdwallet/react-accordion';

import { resetCalculations, resetValidations } from '../../../lib/actions';
import {
  ENTITY_NAME,
  EVENT_TYPE,
  FORM_NAME,
  SECTION_NAME,
  FIELD_NAME,
} from '../../../lib/analytics-constants';
import {
  manualInputsSelector,
  inputsSelector,
  overridesSelector,
  adjustedOutputsSelector,
} from '../../../lib/selectors/home-affordability-calculator-selectors';
import { HomeAffordabilityInput } from '../../../lib/utils';
import { Browser } from '../../../types';
import ControlPanel from '../../config-builder-free/control-panel/control-panel';
import CreditScoreCta from '../../currency/credit-score-cta/credit-score-cta';
import DTISlider from '../../results-panel/dti-slider/dti-slider';
import {
  DownPaymentInput,
  LoanTermInput,
  VeteranEligibilityInput,
  VeteranMortgageInput,
  HouseholdIncomeInput,
  MonthlyDebtInput,
} from '../inputs';

import slides from './auth-slides';
import InterestRateControl from './interest-rate-control/interest-rate-control';
import useAnalytics from '../../../lib/analytics/use-analytics';
import {
  SegmentFormInputEventPayloadT,
  debouncedTrackInputEvent,
} from '../../../lib/analytics/trackers';

import styles from './hac-control-panel.module.less';

// Constants
const { formOpen, formInputChanged } = Tracking;
interface HACControlPanelProps
  extends Pick<
    HomeAffordabilityInput,
    | 'manualInputs'
    | 'inputs'
    | 'dispatch'
    | 'overrides'
    | 'validations'
    | 'adjustedOutputs'
  > {
  enableTracking?: boolean;
  onChange: (config: Record<string, any>, value: any) => void;
  isSignedIn: boolean;
  creditScore: number;
  browser: Browser;
  DTIDetailsConfig: any; // TODO: should update this
  data: any; // TODO: should update this
}

const CONTROL_PANEL = 'controlPanel';

/**
 * Select dropdown for the Home Affordability Cal's loan term input
 * @param
 * @returns ReactNode
 */
const getLoanTermInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_TERM_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      },
    },
  };
  return (
    <LoanTermInput
      analytics={analyticsPayload}
      label="Loan term"
      tooltip="The amount of time you have to pay back the loan. Usually 15 or 30 years for common loans."
    />
  );
};

/**
 * Select dropdown for the Home Affordability Cal's Veteran Eligibility Input
 * @param
 * @returns ReactNode
 */
const getVeteranEligibilityInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.VETERAN_STATUS_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      },
    },
  };
  return (
    <VeteranEligibilityInput
      analytics={analyticsPayload}
      label="Are you a veteran?"
      tooltip="Veterans or spouse of veterans can qualify for 0% down payment mortgages."
    />
  );
};

/**
 * Select dropdown for the Home Affordability Cal's Veteran Mortgage Input
 * @param
 * @returns ReactNode
 */
const getVeteranMortgageInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.VETERAN_STATUS_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      },
    },
  };
  return (
    <VeteranMortgageInput
      analytics={analyticsPayload}
      label="Is this your first VA mortgage?"
    />
  );
};

/**
 * Getter function to determine the Down Payment Label
 * @param data {object}
 * @returns String
 */
const getDownPaymentLabel = (adjustedOutputs, overrides) => {
  const downPayment = adjustedOutputs?.downPayment;
  const fundsNeeded = adjustedOutputs?.fundsNeeded;
  const fundsNeededOverride = overrides?.fundsNeeded;

  if (fundsNeededOverride !== undefined && fundsNeeded > fundsNeededOverride) {
    if (adjustedOutputs?.loanType === LOAN_TYPE_VA) {
      return `Closing costs are ${formatCurrency(
        adjustedOutputs?.closingCosts.total
      )}.`;
    }

    return `Minimum down payment is ${formatPercent(
      adjustedOutputs?.downPaymentPercent,
      '0[.]00%'
    )} (${formatCurrency(adjustedOutputs?.downPayment)}).`;
  }

  return `Down payment (${formatCurrency(downPayment)}) is ${formatPercent(
    adjustedOutputs?.downPaymentPercent,
    '0[.]00%'
  )} of your home price`;
};

/**
 * Down Payment input field
 * @returns InputMoney Node
 */
const getDownPaymentInput = (adjustedOutputs, overrides): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.HOME_DOWN_PAYMENT_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      },
    },
  };

  const mortgageInsurance =
    adjustedOutputs?.monthlyPayments?.mortgageInsurancePayment;
  // Do we have PMI ?
  const showErrorMessage = mortgageInsurance > 0;
  const loanType = adjustedOutputs?.loanType;
  const errorMessage = `${
    loanType !== LOAN_TYPE_FHA ? 'Private mortgage' : 'Mortgage'
  }  insurance has been added to your monthly payment.`;

  return (
    <Stack spacing={0}>
      <DownPaymentInput
        label={getDownPaymentLabel(adjustedOutputs, overrides)}
        analytics={analyticsPayload}
      />
      {showErrorMessage && (
        <Stack spacing={0}>
          <Text size="small" bold>
            Down payment is below 20%
          </Text>
          <Text size="small">{errorMessage}</Text>
        </Stack>
      )}
    </Stack>
  );
};

/**
 * Household income input field
 * @returns InputMoney Node
 */
const getHouseholdIncomeInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.INCOME_PRETAX_INCOME_UPDATED,
        form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      },
    },
  };
  return (
    <Box>
      <Type size="1">
        Annual household income
        <Tooltip
          placement="top"
          content="Your income before taxes. Include your co-borrower’s income if you’re buying a home together."
        />
      </Type>
      <Box className={styles.numStepperWrapper}>
        <HouseholdIncomeInput
          label="Annual household income"
          tooltip="Your income before taxes. Include your co-borrower’s income if you’re buying a home together."
          analytics={analyticsPayload}
          inputType="RANGE"
        />
      </Box>
    </Box>
  );
};

/**
 * Monthly Debt income input field
 * @returns InputMoney Node
 */
const getMonthlyDebtInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
        fieldName: FIELD_NAME.REFI_CALC.CONTROL_PANEL.CLOSINGCOST,
      },
      bedrock: {
        eventType: EVENT_TYPE.MONTHLY_DEBT_UPDATED,
        form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
      },
    },
  };
  return (
    <Box>
      <Type size="1">
        Minimum monthly debt
        <Tooltip
          placement="top"
          content="This only includes the minimum amount you're required to pay each month towards things like child care, car loans, credit card debt, student loans and alimony. If you pay more than the minimum, that's great! But don't include the extra amount you pay."
        />
      </Type>
      <Box className={styles.numStepperWrapper}>
        <MonthlyDebtInput
          label="Minimum monthly debt"
          tooltip="This only includes the minimum amount you're required to pay each month towards things like child care, car loans, credit card debt, student loans and alimony. If you pay more than the minimum, that's great! But don't include the extra amount you pay."
          analytics={analyticsPayload}
          inputType="RANGE"
        />
      </Box>
    </Box>
  );
};

/**
 * Home Affordability Calculator - Control Panel view
 */
const HACControlPanel: React.FC<HACControlPanelProps> = (props) => {
  const segmentAbstract = useAnalytics();
  const { track } = segmentAbstract;
  const {
    enableTracking,
    inputs,
    validations,
    overrides,
    creditScore,
    adjustedOutputs,
    isSignedIn,
    onChange,
    dispatch,
    DTIDetailsConfig,
    data,
    browser,
  } = props;

  const isResetRequired = () => {
    return !_.isEmpty(overrides);
  };

  const onClick = () => {
    formInputChanged({
      formName: FORM_NAME,
      fieldName: 'reset',
      fieldValue: isResetRequired(),
    });

    // segment analytics
    debouncedTrackInputEvent(
      {
        field_value: isResetRequired(),
        field_type: 'click',
        entityName: 'reset',
        field: 'Reset',
      },
      { track }
    );

    if (isResetRequired()) {
      dispatch(resetCalculations());
      dispatch(resetValidations());
    }
  };

  const creditScoreCtaProps = {
    creditScore,
    description:
      'Get free guidance on changes you can make to afford more house, without spending more.',
    flowConfig: { slides },
    header: 'Afford the house you want',
    interestRate: getAnnualInterestRateForCreditScore({
      ...inputs,
      creditScore,
    }),
    isSignedIn,
    tracking: 'hac_credit_score_cta',
  };

  const interestRateControlProps = {
    ...inputs,
    onChange: (cnf, value) => {
      // credit score range selected
      if (cnf.key === 'inputs.creditScoreRangeValue') {
        // segment analytics
        debouncedTrackInputEvent(
          {
            entityName: 'credit_score_category',
            field: 'Credit Score',
            field_type: 'click',
            field_value: value,
            additional_attributes: [
              {
                'Likely Interest Rate': Num.formatPercent(
                  inputs.annualInterestRate
                ),
              },
            ],
          },
          { track }
        );
      }

      // "edit rate" or "Interest rate by credit score" btn clicked
      if (cnf.key === 'inputs.useCreditScoreForInterestRate') {
        // segment analytics
        let evt = null;

        // true value means "Interest rate by credit score" btn clicked
        if (value) {
          evt = {
            entityName: 'interest_rate_by_credit_score',
            field: 'Interest Rate by Credit Score',
            field_type: 'click',
            field_value: value,
          };
        } else {
          evt = {
            field: 'Likely Rate - Edit Rate',
            entityName: 'edit_loan_rate',
            field_type: 'click',
            field_value: value,
          };
        }

        debouncedTrackInputEvent(evt, { track });
      }

      // interest rate percentage changed
      if (cnf.key === 'inputs.annualInterestRate') {
        // segment analytics
        debouncedTrackInputEvent(
          {
            entityName: 'loan_interest_rate',
            field_type: 'user_type_input',
            field_value: value,
            field: 'Percent',
          },
          { track }
        );
      }

      onChange(cnf, value);
    },
    trackingFormName: FORM_NAME,
    // This is outside getViewConfig
    inputId: 'annualInterestRate',
    validations,
  };

  // state to manage accordion's activeKey
  // defaulted to have the first accordion child open
  const [expandedMap, setExpandedMap] = useState([true, false]);

  // accordion's activeKey
  const [activeAccordionChild, setActiveAccordionChild] = useState(0);

  const accordionPropsMap = [
    {
      entityName: ENTITY_NAME.MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE,
      formName: ENTITY_NAME.MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE,
      header: 'Loan details',
      segmentFieldName: 'Loan Details',
    },
    {
      entityName: ENTITY_NAME.MRT_REFI_CALCULATOR_NEW_MORTGAGE,
      formName: ENTITY_NAME.MRT_REFI_CALCULATOR_NEW_MORTGAGE,
      header: 'Income and debts',
      segmentFieldName: 'Income and debts',
    },
  ];

  /**
   * Toggles accordion
   * @param {number} index accordion key/id to open
   */
  const toggleItem = (index: number) => {
    const newExpandedMap = [false, false];
    newExpandedMap[index] = !expandedMap[index];

    const {
      entityName,
      formName,
      header,
      segmentFieldName: field,
    } = accordionPropsMap[index];

    if (enableTracking) {
      formOpen({
        formName,
        fieldName: 'header',
        fieldValue: _.snakeCase(header),
      });
    }

    const uuid = analytics.uuid();
    analytics.track(EVENT_TYPE.ELEMENT_INTERACTED, {
      impression_id: uuid,
      element_name: entityName,
      action: 'open',
      interaction_type: 'press',
    });

    const accordionTrackingPayload: SegmentFormInputEventPayloadT = {
      entityName: 'form_field_section',
      field_value: !expandedMap[index] ? 'expand' : 'contract',
      field_type: 'expand-contract',
      field,
    };

    debouncedTrackInputEvent(accordionTrackingPayload, segmentAbstract);
    setExpandedMap(newExpandedMap);
    setActiveAccordionChild(index);
  };

  // Builds original mortgage accordion content
  const loanDetails = {
    title: <Type size="1">{accordionPropsMap[0].header}</Type>,
    children: (
      <Impression
        enabled={activeAccordionChild === 0}
        eventType={EVENT_TYPE.FORM_IMPRESSION}
        entityName={accordionPropsMap[0].entityName}
        payload={{
          form_name: accordionPropsMap[0].entityName,
          section_name: accordionPropsMap[0].entityName,
        }}
      >
        <Stack spacing={3}>
          <Type size="1">
            Down payment & closing costs
            <Tooltip
              placement="top"
              content="NerdWallet's ratings are determined by our editorial team. The scoring formula takes into account the type of card being reviewed (such as cash back, travel or balance transfer) and the card's rates, fees, rewards and other features."
            />
          </Type>
          {getDownPaymentInput(adjustedOutputs, overrides)}
          {getLoanTermInput()}
          {getVeteranEligibilityInput()}
          {!!inputs?.veteranType && getVeteranMortgageInput()}
        </Stack>
      </Impression>
    ),
    initiallyExpanded: true,
    key: 'old_mortgage_details',
    expanded: expandedMap[0],
    onSelect: () => toggleItem(0),
  };
  // Builds new mortgage accordion content
  const incomeAndDebtDetails = {
    title: <Type size="1"> {accordionPropsMap[1].header} </Type>,
    children: (
      <Impression
        enabled={activeAccordionChild === 1}
        eventType={EVENT_TYPE.FORM_IMPRESSION}
        entityName={accordionPropsMap[1].entityName}
        payload={{
          form_name: accordionPropsMap[1].entityName,
          section_name: accordionPropsMap[1].entityName,
        }}
      >
        <Stack spacing={3}>
          {getHouseholdIncomeInput()}
          {getMonthlyDebtInput()}
        </Stack>
      </Impression>
    ),
    key: 'new_mortgage_details',
    expanded: expandedMap[1],
    onSelect: () => toggleItem(1),
  };

  // Builds entire panel content
  const panelContent = (
    <Stack spacing={3} className={styles.inputFieldWidth}>
      <Impression
        eventType={EVENT_TYPE.FORM_IMPRESSION}
        entityName={ENTITY_NAME.MORTGAGES_HOME_AFFORDABILITY_CALCULATOR_FORM}
        payload={{
          section_name: SECTION_NAME.MRT_HOME_AFFORDABILITY,
          form_name: FORM_NAME.MRT_HOME_AFFORDABILITY_UPDATE,
        }}
      >
        <Box className={styles.panelHeader}>
          <Text bold>Tweak your numbers below</Text>
        </Box>
        <Box className={styles.topPanel}>
          <CreditScoreCta key="credit-score-cta" {...creditScoreCtaProps} />
          <Box className={styles.DTIWrapper}>
            <DTISlider
              DTIDetailsConfig={DTIDetailsConfig}
              data={data}
              browser={browser}
              onChange={onChange}
            />
          </Box>
          <InterestRateControl
            key="credit-score-control"
            {...interestRateControlProps}
          />
        </Box>
      </Impression>
      <ControlledAccordion
        id="mortgage_accordion"
        analytics={{
          enabled: true,
          payload: {
            elementName: ENTITY_NAME.MRT_REFI_CALCULATOR_ORIGINAL_MORTGAGE,
          },
        }}
        items={[loanDetails, incomeAndDebtDetails]}
      />
      <Box className={styles.resetWrapper}>
        <a className={styles.hacResetCalcs} onClick={onClick}>
          <i className={classnames('ss-sync', styles.ssIcon)} />
          <span className={styles.hacResetCalcsButton}>Reset</span>
        </a>
      </Box>
    </Stack>
  );

  return <ControlPanel panelContent={panelContent} />;
};

const mapStateToProps = (state) => ({
  manualInputs: manualInputsSelector(state),
  inputs: inputsSelector(state),
  overrides: overridesSelector(state),
  adjustedOutputs: adjustedOutputsSelector(state),
  isSignedIn: state?.identity?.isSignedIn,
  creditScore:
    state?.identity?.profiles?.tu_credit_report_profile?.vantage_score_3,
});

export default connect(mapStateToProps)(HACControlPanel);
